import React from 'react';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useApolloClient } from '@apollo/client';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { BiFile, BiPlus, BiTime } from 'react-icons/bi';
import {
  BiChevronDown,
  BiChevronUp,
  BiX,
} from 'react-icons/bi';
import { MdCheckCircle, MdDirectionsCar, MdErrorOutline, MdHome, MdPerson } from 'react-icons/md';
import { toast } from 'react-toastify';
import DriverSelect from '@features/drivers/components/DriverSelect';
import { css } from '@emotion/css';

import { Order_By } from '@gql/base-types';
import { GetMobileServiceAppointmentsQuery, useGetMobileServiceAppointmentsQuery } from '../queries/getMobileServiceAppointments.query.generated';
import { useGetServiceTechniciansQuery } from '../queries/getServiceTechnicians.query.generated';
import { useGetMobilityVehiclesQuery } from '../queries/getMobilityVehicles.query.generated';
import { useGetDealershipLocationQuery } from '../queries/getDealershipLocation.query.generated';
import { useGetMobileServiceWorkflowsetsQuery } from '../queries/getMobileServiceWorkflowsets.query.generated';
import { useInsertServiceRunMutation } from '../queries/insertServiceRun.mutation.generated';
import { UpdateMobilityRunEstimatesDocument } from '../queries/updateMobilityRunEstimates.mutation.generated';
import { useRooftop, useUser } from '@store';
import { actorIdentity } from '@utils/actorIdentity';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];

type TechMode = 'manual' | 'auto';
type VehicleMode = 'manual' | 'driver_picks';

interface BuildRunDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialAppointments?: Appointment[];
}

const DRAWER_WIDTH = 420;

/**
 * Fallback per-stop service time for appointments created before the
 * coordinator-entered estimate existed (`config.estimated_service_minutes`,
 * captured in AddAppointmentDialog). Real values come from the appointment;
 * this only covers legacy rows with no stored estimate.
 */
const SERVICE_MINUTES_PER_STOP = 30;

/**
 * Per-stop service minutes: the coordinator-entered estimate stored on the
 * appointment (`config.estimated_service_minutes`), falling back to
 * {@link SERVICE_MINUTES_PER_STOP} for appointments without one. Feeds the
 * summary preview AND the persisted `service_stop_details.estimated_service_minutes`,
 * `service_orders.estimated_minutes`, and `mobility_stops.dwell_time_seconds`.
 */
const serviceMinutesForStop = (appt: { config?: unknown }): number => {
  const minutes = Number((appt.config as { estimated_service_minutes?: unknown })?.estimated_service_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : SERVICE_MINUTES_PER_STOP;
};

type LegLocation = { id: number | null | undefined; location: any };
type ResolvedLeg = { distance_miles: number; duration_sec: number };

/**
 * Full location sequence for a run: dealership → service stops → dealership.
 * Shared by the summary-panel preview and dispatch so both resolve the same legs.
 */
const buildLocationSequence = (
  dealershipLocation: { id: number; [key: string]: any } | null,
  stops: { pickup_location_id?: number | null; pickup?: any }[],
): LegLocation[] => {
  const sequence: LegLocation[] = [];
  if (dealershipLocation) {
    sequence.push({ id: dealershipLocation.id, location: dealershipLocation });
  }
  stops.forEach(s => {
    if (s.pickup_location_id) {
      sequence.push({ id: s.pickup_location_id, location: s.pickup });
    }
  });
  if (dealershipLocation) {
    sequence.push({ id: dealershipLocation.id, location: dealershipLocation });
  }
  return sequence;
};

/**
 * Resolves every consecutive leg of the sequence through the
 * `handleLaneShellPair` netlify function (Google Routes) in parallel.
 * Failures resolve as zero-legs so downstream math still completes.
 *
 * `cache` (keyed `originId:destId`) is shared between the preview effect and
 * dispatch, and stores the *promise* for each leg, set before the request
 * fires: legs already resolved (or still resolving) for the summary preview
 * are never re-fetched when the coordinator hits Dispatch, even if dispatch
 * races an in-flight preview. Failed or zero-duration resolutions are evicted
 * so a later attempt can retry them.
 */
const resolveLegs = async (
  locationSequence: LegLocation[],
  customerId: number,
  token: string,
  cache: Map<string, Promise<ResolvedLeg>>,
): Promise<ResolvedLeg[]> => {
  const baseUrl = window.location.origin;
  const legPromises = locationSequence.slice(0, -1).map((origin, i) => {
    const dest = locationSequence[i + 1];
    const cacheKey = `${origin.id}:${dest.id}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;
    const legPromise = axios({
      method: 'POST',
      url: `${baseUrl}/.netlify/functions/handleLaneShellPair`,
      data: {
        variables: {
          customerId,
          pickupId: origin.id,
          deliveryId: dest.id,
          pickup: origin.location,
          delivery: dest.location,
        },
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).then(
      (response) => {
        const [forward] = Array.isArray(response.data) ? response.data : [null];
        const leg = {
          distance_miles: Number(forward?.distance_miles ?? 0) || 0,
          duration_sec: Number(forward?.duration_sec ?? 0) || 0,
        };
        if (leg.duration_sec <= 0) cache.delete(cacheKey);
        return leg;
      },
      (err) => {
        console.warn(`Failed to resolve lane between stops ${i + 1} and ${i + 2}:`, err);
        cache.delete(cacheKey);
        return { distance_miles: 0, duration_sec: 0 };
      },
    );
    cache.set(cacheKey, legPromise);
    return legPromise;
  });
  return Promise.all(legPromises);
};

/**
 * Computes a sensible default departure as a "YYYY-MM-DDTHH:mm" string for
 * the BuildRunDialog's datetime picker. Prefers the earliest selected
 * appointment's scheduled date+time — so a run built for a future-dated
 * appointment defaults to that day, not today. Appointments in the past
 * (or no appointment time at all) fall back to "now + 30 minutes" rounded
 * up to the next 5-minute mark, so the default is never a past timestamp.
 */
// ISO strings compare lexicographically; appointments without a time sort last
// so timed stops lead the route by default.
const APPOINTMENT_TIME_NULLS_LAST = '9999-12-31T00:00:00Z';
const appointmentTimeKey = (a: Pick<Appointment, 'appointment_time'>): string =>
  a.appointment_time ?? APPOINTMENT_TIME_NULLS_LAST;

/**
 * Chronological default ordering (appointment_time asc, no-time last). The
 * coordinator can still drag stops freely — this only fixes the DEFAULT,
 * which otherwise inherited the appointments table's newest-first sort and
 * produced reverse-chronological routes.
 */
function sortByAppointmentTime<T extends Pick<Appointment, 'appointment_time'>>(list: T[]): T[] {
  return [...list].sort((a, b) => appointmentTimeKey(a).localeCompare(appointmentTimeKey(b)));
}

/**
 * The customer-facing service window persisted to
 * service_stop_details.service_window — formatted in the coordinator's
 * timezone (≈ the dealership's) so the drawer, driver app, and guest
 * confirmation page can all show the promised time.
 */
function appointmentServiceWindow(appt: Pick<Appointment, 'appointment_time'>): string | null {
  return appt.appointment_time ? dayjs(appt.appointment_time).format('MMM D, h:mm A') : null;
}

function computeDefaultDepartureTime(appointments: Pick<Appointment, 'appointment_time'>[]): string {
  const earliestIso = appointments
    .map(a => a.appointment_time)
    .filter((t): t is string => !!t)
    .sort()[0];
  const earliest = earliestIso ? dayjs(earliestIso) : null;
  if (earliest && earliest.isAfter(dayjs())) {
    return earliest.format('YYYY-MM-DDTHH:mm');
  }
  // Round "now + 30 min" up to the next 5-minute boundary.
  const seed = dayjs().add(30, 'minute');
  return seed.minute(Math.ceil(seed.minute() / 5) * 5).second(0).format('YYYY-MM-DDTHH:mm');
}


const BuildRunDialog: React.FC<BuildRunDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialAppointments = [],
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const rooftop = useRooftop();
  const user = useUser();
  const apolloClient = useApolloClient();
  const customerId = rooftop?.id || user?.claims?.['x-hasura-customer-id'] || 0;

  const selectedDriverCls = React.useMemo(() => css`
    background-color: transparent !important;
    border: 2px solid ${theme.palette.primary.main};
    border-radius: 8px;
  `, [theme.palette.primary.main]);

  // ── Data queries
  const { data: driversData, loading: driversLoading } = useGetServiceTechniciansQuery({
    variables: { customerId },
    skip: !customerId || !open,
  });
  const { data: vehiclesData, loading: vehiclesLoading } = useGetMobilityVehiclesQuery({
    variables: { customerId },
    skip: !customerId || !open,
  });
  const { data: workflowsetsData } = useGetMobileServiceWorkflowsetsQuery({
    variables: { customerId },
    skip: !customerId || !open,
  });
  const { data: dealershipData } = useGetDealershipLocationQuery({
    variables: { customerId },
    skip: !customerId || !open,
  });
  const [insertRun, { loading: inserting }] = useInsertServiceRunMutation();

  const drivers = driversData?.drivers ?? [];
  const vehicles = vehiclesData?.mobility_vehicles ?? [];
  // Dealership origin/return comes from the customer's designated store
  // location (customers.location_id → "Location ID of the customer's store"),
  // NOT from a favorite-flagged location. A customer can favorite multiple
  // locations, which made the old `favorite = true` lookup ambiguous and
  // required manually hearting a location before runs would populate.
  const dealershipLocation = dealershipData?.customers_by_pk?.location ?? null;
  // Memoized so their identity is stable across renders — the workflowset
  // auto-select effect below depends on `preferredWorkflowsets`, and an inline
  // `.filter()` would hand it a new array every render.
  const allWorkflowsets = React.useMemo(() => workflowsetsData?.workflowsets ?? [], [workflowsetsData]);
  const preferredWorkflowsets = React.useMemo(
    () => allWorkflowsets.filter(ws => ws.mobile_inspection_workflow_id),
    [allWorkflowsets],
  );
  const otherWorkflowsets = React.useMemo(
    () => allWorkflowsets.filter(ws => !ws.mobile_inspection_workflow_id),
    [allWorkflowsets],
  );

  // ── Local state
  const [techMode, setTechMode] = React.useState<TechMode>('manual');
  const [selectedDriverId, setSelectedDriverId] = React.useState<number | null>(null);
  const [vehicleMode, setVehicleMode] = React.useState<VehicleMode>('manual');
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<number | null>(null);
  const [workflowsetId, setWorkflowsetId] = React.useState<number>(0);
  const [departureTime, setDepartureTime] = React.useState<string>(() =>
    computeDefaultDepartureTime(initialAppointments),
  );
  const [dispatching, setDispatching] = React.useState(false);
  const [stops, setStops] = React.useState<Appointment[]>([]);
  const [dispatchResult, setDispatchResult] = React.useState<{
    runId: number;
    driverName: string | null;
    stopNames: string[];
  } | null>(null);

  // Auto-select preferred workflowset when data loads
  React.useEffect(() => {
    if (open && preferredWorkflowsets.length > 0 && workflowsetId === 0) {
      setWorkflowsetId(preferredWorkflowsets[0].id);
    }
  }, [open, preferredWorkflowsets, workflowsetId]);

  // The dialog resets its form each time it OPENS, seeding stops from the
  // appointments the caller passed. We can't simply key this effect on
  // `initialAppointments`: callers pass an inline array (a default `[]` or a
  // fresh `.filter(...)`) whose identity changes every render, which re-ran the
  // reset — and its setState calls — on every render → "Maximum update depth
  // exceeded". Instead, only reset on the closed→open transition.
  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setStops(sortByAppointmentTime(initialAppointments));
      setTechMode('manual');
      setSelectedDriverId(null);
      setVehicleMode('manual');
      setSelectedVehicleId(null);
      setWorkflowsetId(0);
      setDispatching(false);
      setDepartureTime(computeDefaultDepartureTime(initialAppointments));
      setDispatchResult(null);
      // Leg cache is scoped to one open of the dialog: between opens the
      // rooftop or a stop's location may have changed, so cached (and
      // possibly still-in-flight) leg resolutions would be stale.
      legCacheRef.current.clear();
    }
    prevOpenRef.current = open;
  }, [open, initialAppointments]);

  // ── Available appointments to pick as stops
  const [appointmentPickerAnchor, setAppointmentPickerAnchor] = React.useState<HTMLElement | null>(null);
  const dateRange = React.useMemo(() => ({
    start: dayjs().subtract(90, 'day').startOf('day').toISOString(),
    end: dayjs().add(30, 'day').endOf('day').toISOString(),
  }), []);
  const { data: appointmentsData, loading: appointmentsLoading } = useGetMobileServiceAppointmentsQuery({
    variables: {
      customerId,
      start: dateRange.start,
      end: dateRange.end,
      limit: 200,
      offset: 0,
      orderBy: [{ appointment_time: Order_By.DescNullsLast }, { id: Order_By.DescNullsLast }],
      typeFilter: { type: { _eq: 'mobile_service' } },
    },
    fetchPolicy: 'cache-and-network',
    skip: !customerId || !open,
  });

  const availableAppointments = React.useMemo(() => {
    const stopIds = new Set(stops.map(s => s.id));
    // Chronological for run building (the query's newest-first order suits
    // the appointments table, not a route picker).
    return sortByAppointmentTime(
      (appointmentsData?.appointments ?? []).filter(a => !stopIds.has(a.id))
    );
  }, [appointmentsData, stops]);

  const addAppointmentToStops = (appt: Appointment) => {
    // Insert at the chronological position instead of appending, so picking
    // stops in any order still yields a time-sensible default route. Manual
    // drag ordering is untouched — this only chooses the landing spot.
    setStops(prev => {
      const key = appointmentTimeKey(appt);
      const idx = prev.findIndex(s => appointmentTimeKey(s) > key);
      const next = [...prev];
      next.splice(idx === -1 ? next.length : idx, 0, appt);
      return next;
    });
    setAppointmentPickerAnchor(null);
  };

  // ── Stop management
  // Soft mismatch check: a departure AFTER the earliest promised appointment
  // time can't make that window. Warn, never block — service windows are
  // dealer-driven and the coordinator may be rescheduling deliberately.
  const earliestAppointmentTime = React.useMemo(() => {
    const times = stops
      .map(s => s.appointment_time)
      .filter((t): t is string => Boolean(t))
      .sort();
    return times[0] ?? null;
  }, [stops]);
  const departureAfterAppointment = Boolean(
    earliestAppointmentTime &&
    departureTime &&
    dayjs(departureTime).isValid() &&
    dayjs(departureTime).isAfter(dayjs(earliestAppointmentTime))
  );

  const moveStop = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= stops.length) return;
    setStops(prev => {
      const arr = [...prev];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr;
    });
  };

  const removeStop = (id: number) => {
    setStops(prev => prev.filter(s => s.id !== id));
  };

  // ── Validation: stops missing location data
  const stopsWithoutLocation = React.useMemo(
    () => stops.filter(s => !s.pickup_location_id),
    [stops]
  );

  // ── Validation: required selections. The Dispatch button stays disabled
  // (with a "To dispatch: …" hint) until every one is satisfied; the toasts
  // in handleDispatch remain as a backstop. Workflow is required here even
  // though profile_id is nullable server-side — a run without one gives the
  // tech no inspection checklist.
  const missingRequirements = React.useMemo(() => {
    const missing: string[] = [];
    if (stops.length === 0) missing.push('add at least one stop');
    if (techMode === 'manual' && !selectedDriverId) missing.push('select a technician');
    if (vehicleMode === 'manual' && !selectedVehicleId) missing.push('select a vehicle');
    if (!workflowsetId) missing.push('select a workflow');
    if (!departureTime || !dayjs(departureTime).isValid()) missing.push('set a departure time');
    return missing;
  }, [stops.length, techMode, selectedDriverId, vehicleMode, selectedVehicleId, workflowsetId, departureTime]);

  // ── Run summary calculations
  // These values feed both the preview shown to the coordinator AND the
  // per-stop estimates persisted by the insert mutation below, so they
  // stay in lockstep with what the driver sees on their offer / run card.
  const totalServiceMinutes = React.useMemo(() => {
    return stops.reduce((sum, s) => sum + serviceMinutesForStop(s), 0);
  }, [stops]);

  // Real drive-time preview: resolve the run's legs through Google Routes
  // (same handleLaneShellPair path dispatch uses) whenever the stop
  // sequence changes. Debounced so rapid reordering doesn't fan out
  // duplicate requests; the per-pair cache is shared with dispatch so legs
  // resolved here aren't re-fetched on Dispatch.
  const legCacheRef = React.useRef(new Map<string, Promise<ResolvedLeg>>());
  const [previewDriveSeconds, setPreviewDriveSeconds] = React.useState<number | null>(null);
  const [previewDriveLoading, setPreviewDriveLoading] = React.useState(false);
  const previewEpochRef = React.useRef(0);

  React.useEffect(() => {
    const epoch = ++previewEpochRef.current;
    const sequence = buildLocationSequence(dealershipLocation, stops);
    if (!open || sequence.length < 2 || !customerId) {
      setPreviewDriveSeconds(null);
      setPreviewDriveLoading(false);
      return;
    }
    setPreviewDriveLoading(true);
    const timer = setTimeout(async () => {
      try {
        const token = await user?.getToken();
        if (!token || previewEpochRef.current !== epoch) return;
        const legs = await resolveLegs(sequence, customerId, token, legCacheRef.current);
        if (previewEpochRef.current !== epoch) return; // stale — stops changed underneath us
        const totalSeconds = legs.reduce((s, l) => s + l.duration_sec, 0);
        // All-zero legs means every resolution failed; treat as unresolved
        // so the UI falls back to the rough estimate instead of "0 min".
        setPreviewDriveSeconds(totalSeconds > 0 ? totalSeconds : null);
      } catch (err) {
        console.warn('Failed to resolve preview drive time:', err);
        if (previewEpochRef.current === epoch) setPreviewDriveSeconds(null);
      } finally {
        if (previewEpochRef.current === epoch) setPreviewDriveLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stops, dealershipLocation, customerId]);

  const estDriveMinutes = React.useMemo(() => {
    if (previewDriveSeconds != null) return Math.round(previewDriveSeconds / 60);
    // Fallback rough estimate while legs resolve (or when resolution fails).
    return stops.length > 0 ? (stops.length - 1) * 12 + 12 : 0;
  }, [previewDriveSeconds, stops]);

  const estTotalHours = React.useMemo(() => {
    const totalMin = totalServiceMinutes + estDriveMinutes;
    return totalMin > 0 ? (totalMin / 60).toFixed(1) : '0';
  }, [totalServiceMinutes, estDriveMinutes]);

  // ── Submit
  const handleDispatch = async () => {
    if (stops.length === 0) {
      toast.warning('Add at least one stop to the run');
      return;
    }
    if (stopsWithoutLocation.length > 0) {
      toast.error(`${stopsWithoutLocation.length} stop${stopsWithoutLocation.length !== 1 ? 's are' : ' is'} missing a service address. Edit the appointment to add an address before dispatching.`);
      return;
    }
    if (techMode === 'manual' && !selectedDriverId) {
      toast.warning('Select a technician');
      return;
    }
    if (vehicleMode === 'manual' && !selectedVehicleId) {
      toast.warning('Select a vehicle or choose "Technician Selects at Start"');
      return;
    }
    if (!workflowsetId) {
      toast.warning('Select a workflow');
      return;
    }

    // Departure comes from a datetime-local input; dayjs parses the
    // offset-less string with local-time semantics on every platform.
    const departureDayjs = dayjs(departureTime);
    if (!departureTime || !departureDayjs.isValid()) {
      toast.warning('Set a departure date and time');
      return;
    }

    setDispatching(true);
    const now = new Date().toISOString();
    const email = actorIdentity(user);

    const departure = departureDayjs.second(0).millisecond(0).toDate();

    try {
      const result = await insertRun({
        variables: {
          run: {
            product_type: 'mobile_service',
            status: 'scheduled',
            customer_id: customerId,
            profile_id: workflowsetId || null,
            driver_id: null,
            vehicle_id: vehicleMode === 'manual' ? selectedVehicleId : null,
            departure_time: departure.toISOString(),
            config: {
              technician_mode: techMode,
              vehicle_mode: vehicleMode,
              source_appointment_ids: stops.map(s => s.id),
            },
            created_at: now,
            created_by: email,
            updated_at: now,
            updated_by: email,
            stops: {
              data: [
                // Departure from dealership — no service work, zero dwell.
                ...(dealershipLocation ? [{
                  product_type: 'mobile_service',
                  status: 'pending_confirmation',
                  stop_order: 1,
                  location_id: dealershipLocation.id,
                  address: dealershipLocation.address || null,
                  address_lat: dealershipLocation.latitude || null,
                  address_lng: dealershipLocation.longitude || null,
                  notes: 'Depart from dealership',
                  dwell_time_seconds: 0,
                }] : []),
                // Service stops — each gets a per-stop service-time estimate
                // on both service_stop_detail and service_orders, plus a
                // dwell-time match in seconds on the stop itself. drive_time
                // is back-filled after lane resolution.
                ...stops.map((appt, idx) => ({
                  product_type: 'mobile_service',
                  status: 'pending_confirmation',
                  stop_order: idx + (dealershipLocation ? 2 : 1),
                  location_id: appt.pickup_location_id || null,
                  address: appt.pickup?.address || null,
                  address_lat: appt.pickup?.latitude || null,
                  address_lng: appt.pickup?.longitude || null,
                  notes: appt.driver_notes,
                  dwell_time_seconds: serviceMinutesForStop(appt) * 60,
                  service_stop_detail: {
                    data: [{
                      customer_name: appt.consumer_name,
                      customer_phone: appt.consumer_phone,
                      service_description: appt.driver_notes,
                      ro_reference: appt.ro_number,
                      estimated_service_minutes: serviceMinutesForStop(appt),
                      // Carry the promised appointment time onto the stop —
                      // the drawer, driver app, and guest confirmation page
                      // all read service_window.
                      service_window: appointmentServiceWindow(appt),
                    }],
                  },
                  service_orders: {
                    data: [{
                      // Links the service order back to the source appointment so
                      // event-handler jobs can drive appointment status transitions
                      // (pending -> ready -> completed/missed) from stop-level events.
                      appointment_id: appt.id,
                      vehicle_vin: appt.vehicle_vin,
                      vehicle_make: appt.vehicle_make,
                      vehicle_model: appt.vehicle_model,
                      vehicle_year: appt.vehicle_year,
                      vehicle_color: appt.vehicle_color,
                      service_description: appt.driver_notes || 'Mobile service',
                      status: 'pending',
                      estimated_minutes: serviceMinutesForStop(appt),
                    }],
                  },
                })),
                // Return to dealership — no service work, zero dwell.
                ...(dealershipLocation ? [{
                  product_type: 'mobile_service',
                  status: 'pending_confirmation',
                  stop_order: stops.length + 2,
                  location_id: dealershipLocation.id,
                  address: dealershipLocation.address || null,
                  address_lat: dealershipLocation.latitude || null,
                  address_lng: dealershipLocation.longitude || null,
                  notes: 'Return to dealership',
                  dwell_time_seconds: 0,
                }] : []),
              ],
            },
          },
        },
      });

      const createdRunId = result.data?.insert_mobility_runs_one?.id;
      const selectedDriver = drivers.find(d => d.id === selectedDriverId);

      // Resolve lanes between all consecutive stops, then aggregate
      // distance + duration into run-level + per-stop estimates. This
      // replaces the previous fire-and-forget loop: we now await the
      // (already synchronous) lane resolution so the resulting Google
      // Routes data can be persisted into mobility_runs / mobility_stops
      // for the driver-app offer card and service card. handleLaneShellPair
      // is blocking on Google Routes per call, so this typically takes
      // ~1-3 seconds for a 5-stop run with parallel-fetch.
      {
        // Build the full location sequence: dealership → service stops → dealership
        const locationSequence = buildLocationSequence(dealershipLocation, stops);

        const insertedStops = result.data?.insert_mobility_runs_one?.stops ?? [];

        if (locationSequence.length > 1 && createdRunId) {
          const token = await user?.getToken();
          if (token) {
            // Resolve every leg in parallel (shared cache with the summary
            // preview, so legs already resolved there return instantly).
            const legs = await resolveLegs(locationSequence, customerId, token, legCacheRef.current);

            // Aggregate totals. Service minutes sum each stop's
            // coordinator-entered estimate (fallback 30 for legacy rows).
            const totalDistanceMiles = legs.reduce((s, l) => s + l.distance_miles, 0);
            const totalDriveSeconds = legs.reduce((s, l) => s + l.duration_sec, 0);
            const totalServiceMinutes = stops.reduce((sum, s) => sum + serviceMinutesForStop(s), 0);
            const totalDurationMinutes = totalServiceMinutes + Math.round(totalDriveSeconds / 60);
            const expectedReturnTime = dayjs(departure.toISOString())
              .add(totalDurationMinutes, 'minute')
              .toISOString();

            // Per-stop drive_time_seconds: stop[i]'s inbound leg is
            // legs[i-1]. Stop 0 (dealership departure) has no inbound, so 0.
            // dwell_time_seconds was already set at insert time; we don't
            // need to update it here.
            const stopUpdates = insertedStops.map((stop: any, idx: number) => ({
              where: { id: { _eq: stop.id } },
              _set: {
                drive_time_seconds: idx === 0 ? 0 : (legs[idx - 1]?.duration_sec ?? 0),
              },
            }));

            try {
              await apolloClient.mutate({
                mutation: UpdateMobilityRunEstimatesDocument,
                variables: {
                  runId: createdRunId,
                  estimatedDurationMinutes: totalDurationMinutes,
                  estimatedDistanceMiles: Number(totalDistanceMiles.toFixed(2)),
                  expectedReturnTime,
                  stopUpdates,
                },
              });
            } catch (estimateErr) {
              // Non-fatal — proceed to dispatch anyway. The driver will see
              // an offer card with empty estimates, but the lifecycle works.
              console.warn('Failed to write run estimates:', estimateErr);
            }
          }
        }
      }

      // Dispatch the run (create bundle + offer). Manual mode targets a specific
      // driver via driverIdOverride; auto mode broadcasts to available drivers.
      // Driver assignment lands on the run only after offer.assigned fires.
      if (createdRunId) {
        try {
          const token = await user?.getToken();
          if (token) {
            const baseUrl = window.location.origin;
            const dispatchRes = await axios({
              method: 'POST',
              url: `${baseUrl}/.netlify/functions/dispatchMobileServiceRun`,
              data: {
                runId: createdRunId,
                targetDriverId: techMode === 'manual' ? selectedDriverId : null,
              },
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (!dispatchRes.data?.success) {
              console.warn('Dispatch function returned non-success:', dispatchRes.data);
            }
          }
        } catch (dispatchErr: any) {
          // Run was created but dispatch failed. Show warning, don't block confirmation.
          console.error('Failed to dispatch run:', dispatchErr);
          toast.warning('Run created but dispatch failed. You can re-dispatch from the run detail page.');
        }
      }

      setDispatchResult({
        runId: createdRunId ?? 0,
        driverName: techMode === 'manual' ? (selectedDriver?.user?.display_name ?? null) : null,
        stopNames: stops.map(s => s.consumer_name || 'Unknown'),
      });
    } catch (err: any) {
      toast.error(`Failed to create run: ${err?.message || 'Unknown error'}`);
    } finally {
      setDispatching(false);
    }
  };

  const handleBackToQueue = () => {
    onSuccess();
  };

  // ── Confirmation view (stays as a centered dialog)
  if (dispatchResult) {
    return (
      <Dialog open={open} onClose={handleBackToQueue} maxWidth='sm' fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, px: 2 }}>
            <MdCheckCircle size={48} color='#20b020' />

            <Typography variant='h5' sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
              Service Run Dispatched!
            </Typography>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 2.5 }}>
              {dispatchResult.stopNames.length} stop{dispatchResult.stopNames.length !== 1 ? 's' : ''}
              {dispatchResult.driverName ? ` assigned to ${dispatchResult.driverName}` : ', pending auto-assignment'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
              {dispatchResult.stopNames.map((name, idx) => (
                <Chip
                  key={idx}
                  avatar={
                    <Avatar sx={{ bgcolor: 'secondaryAccent.main', width: 22, height: 22, fontSize: 11, fontWeight: 700 }}>
                      {idx + 1}
                    </Avatar>
                  }
                  label={name}
                  size='small'
                  variant='outlined'
                  sx={{ fontWeight: 500 }}
                />
              ))}
            </Box>

            <Divider sx={{ width: '100%', mb: 2.5 }} />

            <Typography variant='body2' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
              Technician notified via push notification. Customer questionnaires and notifications sent automatically.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant='outlined'
                onClick={handleBackToQueue}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Back to Queue
              </Button>
              <Button
                variant='contained'
                onClick={() => { onSuccess(); navigate(`/mobile-service/runs/${dispatchResult.runId}${location.search}`); }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                View Run
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          Build Service Run
        </Typography>
        <IconButton onClick={onClose} size='small'>
          <BiX size={20} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {/* ── ASSIGN TECHNICIAN ── */}
        <Box sx={{ mb: 3 }}>
          <DriverSelect
            drivers={drivers}
            selectedDriverId={techMode === 'manual' ? selectedDriverId : null}
            onSelect={(id) => {
              if (id) {
                setTechMode('manual');
                setSelectedDriverId(id);
              } else {
                setSelectedDriverId(null);
              }
            }}
            label='Assign Technician'
            filterPlaceholder='Search technicians...'
            maxHeight={200}
            loading={driversLoading}
            emptyMessage='No drivers found for this customer'
            selectedClassName={selectedDriverCls}
          />

          {/* Auto-assign option — temporarily disabled; will be implemented
              later. Underlying techMode='auto' branch + dispatch payload
              fields are intentionally left intact so re-enabling is just
              uncommenting this block. */}
          {/*
          <Card
            variant='outlined'
            sx={{
              mt: 1,
              border: 2,
              borderColor: techMode === 'auto' ? 'primary.main' : 'divider',
              transition: 'border-color 0.15s',
            }}
          >
            <CardActionArea
              onClick={() => { setTechMode('auto'); setSelectedDriverId(null); }}
              sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'flex-start', gap: 1.5 }}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'info.main', fontSize: 16 }}>
                <MdBolt />
              </Avatar>
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>Auto-assign Technician</Typography>
                <Typography variant='caption' color='text.secondary'>
                  System assigns next available technician at dispatch
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
          */}
        </Box>

        {/* ── SERVICE VEHICLE ── */}
        <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
          Service Vehicle
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, mb: 3 }}>
          {vehiclesLoading ? (
            <Typography variant='body2' color='text.secondary'>Loading vehicles...</Typography>
          ) : (
            vehicles.map(vehicle => (
              <Card
                key={vehicle.id}
                variant='outlined'
                sx={{
                  border: 2,
                  borderColor: vehicleMode === 'manual' && selectedVehicleId === vehicle.id
                    ? 'primary.main' : 'divider',
                  transition: 'border-color 0.15s',
                }}
              >
                <CardActionArea
                  onClick={() => { setVehicleMode('manual'); setSelectedVehicleId(vehicle.id); }}
                  sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'flex-start', gap: 1.5 }}
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.200', color: 'text.secondary' }}>
                    <MdDirectionsCar size={20} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {vehicle.license_plate || vehicle.vin}
                    </Typography>
                  </Box>
                  {/* Currently out on an active run (syncVehicleStatus keeps
                      this fresh). Still selectable — planning a later run
                      with today's van is legitimate. */}
                  {vehicle.status === 'in_use' ? (
                    <Chip
                      label='In Use'
                      size='small'
                      color='warning'
                      sx={{ fontSize: 10, fontWeight: 600, height: 20, flexShrink: 0 }}
                    />
                  ) : null}
                </CardActionArea>
              </Card>
            ))
          )}

          {/* Technician selects */}
          <Card
            variant='outlined'
            sx={{
              border: 2,
              borderColor: vehicleMode === 'driver_picks' ? 'primary.main' : 'divider',
              transition: 'border-color 0.15s',
            }}
          >
            <CardActionArea
              onClick={() => { setVehicleMode('driver_picks'); setSelectedVehicleId(null); }}
              sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'flex-start', gap: 1.5 }}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.200', color: 'text.secondary' }}>
                <MdPerson size={20} />
              </Avatar>
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>Technician Selects at Start</Typography>
                <Typography variant='caption' color='text.secondary'>
                  Driver picks their vehicle when they claim the run
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        </Box>

        {/* ── WORKFLOW ── */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
            Workflow
          </Typography>
          <TextField
            fullWidth
            select
            size='small'
            value={workflowsetId || 0}
            onChange={e => setWorkflowsetId(parseInt(e.target.value) || 0)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <BiFile size={18} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mt: 0.5 }}
          >
            <MenuItem value={0} sx={{ color: 'text.secondary' }}>
              Select a workflow...
            </MenuItem>
            {preferredWorkflowsets.length > 0 ? (
              <ListSubheader sx={{ fontSize: 11, fontWeight: 700, lineHeight: '28px', color: 'text.secondary', letterSpacing: 0.5 }}>
                RECOMMENDED
              </ListSubheader>
            ) : null}
            {preferredWorkflowsets.map(ws => (
              <MenuItem key={ws.id} value={ws.id} sx={{ display: 'block' }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {ws.name}
                </Typography>
                {ws.description ? (
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {ws.description}
                  </Typography>
                ) : null}
              </MenuItem>
            ))}
            {otherWorkflowsets.length > 0 ? (
              <ListSubheader sx={{ fontSize: 11, fontWeight: 700, lineHeight: '28px', color: 'text.disabled', letterSpacing: 0.5 }}>
                OTHER
              </ListSubheader>
            ) : null}
            {otherWorkflowsets.map(ws => (
              <MenuItem key={ws.id} value={ws.id} sx={{ display: 'block' }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {ws.name}
                </Typography>
                {ws.description ? (
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {ws.description}
                  </Typography>
                ) : null}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* ── DEPARTURE TIME ── */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
            Departure Time
          </Typography>
          <TextField
            fullWidth
            type='datetime-local'
            size='small'
            value={departureTime}
            onChange={e => setDepartureTime(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <BiTime size={18} />
                  </InputAdornment>
                ),
              },
              htmlInput: {
                step: 300, // 5-minute increments
              },
            }}
            helperText={departureAfterAppointment
              ? `Departure is after the earliest appointment (${dayjs(earliestAppointmentTime!).format('MMM D, h:mm A')}) — the run may miss its window.`
              : 'When the technician will leave the dealership.'}
            sx={{
              mt: 0.5,
              ...(departureAfterAppointment
                ? { '& .MuiFormHelperText-root': { color: 'warning.main', fontWeight: 600 } }
                : {}),
            }}
          />
        </Box>

        {/* ── ROUTE STOPS ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
            Route Stops ({stops.length + (dealershipLocation && stops.length > 0 ? 2 : 0)})
          </Typography>
          <Button
            size='small'
            startIcon={<BiPlus />}
            onClick={e => setAppointmentPickerAnchor(e.currentTarget)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add Stop
          </Button>
        </Box>
        <Popover
          open={Boolean(appointmentPickerAnchor)}
          anchorEl={appointmentPickerAnchor}
          onClose={() => setAppointmentPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              Add appointment as stop
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Mobile service appointments
            </Typography>
          </Box>
          <Box sx={{ overflowY: 'auto', maxHeight: 340 }}>
            {appointmentsLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : availableAppointments.length === 0 ? (
              <Typography variant='body2' color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
                No available appointments.
              </Typography>
            ) : (
              availableAppointments.map(appt => (
                <CardActionArea
                  key={appt.id}
                  onClick={() => addAppointmentToStops(appt)}
                  sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {appt.consumer_name || 'Unknown'} <Typography component='span' variant='caption' color='text.secondary'>#{appt.id}</Typography>
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {appt.appointment_time ? dayjs(appt.appointment_time).format('MMM D · h:mm A') : 'No time set'}
                    {appt.pickup?.address ? ` · ${appt.pickup.address}` : ''}
                  </Typography>
                </CardActionArea>
              ))
            )}
          </Box>
        </Popover>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {/* Departure from dealership */}
          {dealershipLocation && stops.length > 0 ? (
            <Card variant='outlined' sx={{ px: 2, py: 1.25, borderStyle: 'dashed' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'grey.300',
                    color: 'text.secondary',
                    fontSize: 14,
                  }}
                >
                  <MdHome />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Depart from Dealership
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {dealershipLocation.name || dealershipLocation.address}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ) : null}

          {/* No store location on the customer record — depart/return stops
              can't be built. Surface this instead of silently dropping them. */}
          {!dealershipLocation && stops.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.5, py: 1, borderRadius: 1, bgcolor: 'warning.50', border: 1, borderColor: 'warning.200' }}>
              <MdErrorOutline size={16} color={theme.palette.warning.main} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography variant='caption' color='warning.main' sx={{ fontWeight: 500 }}>
                No store location set for this customer. The run won&rsquo;t include a
                depart/return leg. Set the customer&rsquo;s store location to enable it.
              </Typography>
            </Box>
          ) : null}

          {stops.map((stop, idx) => (
            <Card key={stop.id} variant='outlined' sx={{ px: 2, py: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'secondaryAccent.main',
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {idx + 1}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {stop.consumer_name || 'Unknown'}
                  </Typography>
                  {stop.pickup?.address ? (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {stop.pickup.address} &middot; ~{serviceMinutesForStop(stop)}min
                    </Typography>
                  ) : (
                    <Chip
                      icon={<MdErrorOutline size={14} />}
                      label='No address — edit appointment'
                      size='small'
                      color='error'
                      variant='outlined'
                      sx={{ mt: 0.25, height: 22, fontSize: 11 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  <IconButton size='small' onClick={() => moveStop(idx, idx - 1)} disabled={idx === 0}>
                    <BiChevronUp size={18} />
                  </IconButton>
                  <IconButton size='small' onClick={() => moveStop(idx, idx + 1)} disabled={idx === stops.length - 1}>
                    <BiChevronDown size={18} />
                  </IconButton>
                  <IconButton size='small' onClick={() => removeStop(stop.id)} sx={{ color: 'error.main' }}>
                    <BiX size={18} />
                  </IconButton>
                </Box>
              </Box>
            </Card>
          ))}

          {/* Return-to-dealership stop */}
          {dealershipLocation && stops.length > 0 ? (
            <Card variant='outlined' sx={{ px: 2, py: 1.25, borderStyle: 'dashed' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'grey.300',
                    color: 'text.secondary',
                    fontSize: 14,
                  }}
                >
                  <MdHome />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Return to Dealership
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {dealershipLocation.name || dealershipLocation.address}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ) : null}

          {stops.length === 0 ? (
            <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
              No stops in this run. Click &ldquo;Add Stop&rdquo; above to pick appointments.
            </Typography>
          ) : null}
        </Box>

        {/* ── RUN SUMMARY ── */}
        {stops.length > 0 ? (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant='body2' color='text.secondary'>Total service time:</Typography>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>~{totalServiceMinutes} min</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='body2' color='text.secondary'>Est. drive time:</Typography>
              <Typography variant='body2' sx={{ fontWeight: 500, color: previewDriveLoading ? 'text.disabled' : undefined }}>
                {previewDriveLoading ? 'Calculating…' : `~${estDriveMinutes} min`}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>Est. total run:</Typography>
              <Typography variant='body2' sx={{ fontWeight: 700 }}>{estTotalHours} hours</Typography>
            </Box>
          </Box>
        ) : null}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        {stopsWithoutLocation.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1, py: 1, borderRadius: 1, bgcolor: 'error.50', border: 1, borderColor: 'error.200' }}>
            <MdErrorOutline size={16} color={theme.palette.error.main} />
            <Typography variant='caption' color='error.main' sx={{ fontWeight: 500 }}>
              {stopsWithoutLocation.length} stop{stopsWithoutLocation.length !== 1 ? 's' : ''} missing address — cannot dispatch
            </Typography>
          </Box>
        )}
        {missingRequirements.length > 0 && stopsWithoutLocation.length === 0 && (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
            To dispatch: {missingRequirements.join(' · ')}
          </Typography>
        )}
        <Button
          fullWidth
          variant='contained'
          size='large'
          onClick={handleDispatch}
          disabled={dispatching || inserting || missingRequirements.length > 0 || stopsWithoutLocation.length > 0}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.5 }}
        >
          {dispatching
            ? <><CircularProgress size={18} color='inherit' sx={{ mr: 1 }} /> Creating Run...</>
            : `Dispatch Run (${stops.length + (dealershipLocation && stops.length > 0 ? 2 : 0)} stops)`
          }
        </Button>
      </Box>
    </Drawer>
  );
};

export default BuildRunDialog;
